// Name and synonym search, ranked exact > prefix > word prefix > substring > subsequence.
// Ported from the three.js app (archive/engine-web-threejs, src/app/search.ts).

import type { Structure } from './catalogue.ts';

export interface Hit {
  s: Structure;
  score: number;
  /** The name or synonym that matched. */
  matched: string;
}

export const norm = (t: string) =>
  t
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

function scoreOne(q: string, text: string): number {
  const t = norm(text);
  if (!t || !q) return 0;
  if (t === q) return 100;
  if (t.startsWith(q)) return 80 - Math.min(20, t.length - q.length) * 0.2;
  const words = t.split(' ');
  const qWords = q.split(' ');
  // Every query word is a prefix of some word ("left hipp" and "hipp left" both find Left hippocampus).
  if (qWords.every((qw) => words.some((w) => w.startsWith(qw)))) return 60 - Math.min(20, t.length - q.length) * 0.2;
  const i = t.indexOf(q);
  if (i >= 0) return 40 - i * 0.5;
  // Subsequence, so "hpcmps" still finds hippocampus.
  let k = 0;
  for (const ch of t) if (ch === q[k]) k++;
  if (k === q.length && q.length >= 3) return 15 - (t.length - q.length) * 0.1;
  return 0;
}

export function search(structures: readonly Structure[], query: string, limit = 12): Hit[] {
  const q = norm(query);
  if (!q) return [];
  const hits: Hit[] = [];
  for (const s of structures) {
    let best = 0;
    let matched = s.name;
    const texts: [string, number][] = [[s.name, 1], ...s.synonyms.map((x): [string, number] => [x, 0.95]), [s.id.replace(/^ba:/, ''), 0.9], [s.system, 0.5]];
    for (const [text, bonus] of texts) {
      const sc = scoreOne(q, text) * bonus;
      if (sc > best) {
        best = sc;
        matched = text;
      }
    }
    if (best > 0) hits.push({ s, score: best, matched });
  }
  return hits.sort((a, b) => b.score - a.score || a.s.name.localeCompare(b.s.name)).slice(0, limit);
}

function editDistance(a: string, b: string): number {
  const dp = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]!;
    dp[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j]!;
      dp[j] = Math.min(dp[j]! + 1, dp[j - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[b.length]!;
}

/** The names closest to the query by edit distance, for "did you mean" when nothing matches. */
export function nearestNames(structures: readonly Structure[], query: string, limit = 3): Structure[] {
  const q = norm(query);
  if (!q) return [];
  const score = (s: Structure) =>
    Math.min(
      ...[s.name, ...s.synonyms].map((t) => {
        const n = norm(t);
        return Math.min(editDistance(q, n), editDistance(q, n.slice(0, q.length)) + 0.5);
      }),
    );
  return [...structures].sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name)).slice(0, limit);
}
